import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getSuperTicketTemplates,
  previewSuperTicket,
  saveSuperTicketTemplate,
  type SuperTicketOrganizationScope,
  type SuperTicketPreviewInput,
} from './super-ticket-settings.api'

const root = (organizationId: OrganizationIdInput): AdminQueryRoot =>
  buildAdminQueryRoot({ entity: 'super-ticket-settings' }, { organizationId })

export const superTicketQueries = {
  catalog: ({ organizationId }: SuperTicketOrganizationScope) =>
    queryOptions({
      queryKey: buildScopedAdminQueryKey(root(organizationId), {
        organizationId,
        scope: 'catalog',
      }),
      queryFn: ({ signal }) => getSuperTicketTemplates({ organizationId, signal }),
      staleTime: 0,
    }),
  preview: ({ organizationId, template, layout, salesOrderId }: SuperTicketPreviewInput) =>
    queryOptions({
      queryKey: buildScopedAdminQueryKey(root(organizationId), {
        organizationId,
        scope: 'preview',
        template,
        layout,
        salesOrderId,
      }),
      queryFn: ({ signal }) =>
        previewSuperTicket({ organizationId, template, layout, salesOrderId, signal }),
      staleTime: 0,
      gcTime: 60_000,
      retry: false,
    }),
}

export function useSaveSuperTicketTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: saveSuperTicketTemplate,
    onSuccess: async (saved, { organizationId }) => {
      const catalog = superTicketQueries.catalog({ organizationId })
      await queryClient.cancelQueries({ queryKey: catalog.queryKey })
      // Keep the confirmed layout/version even if the follow-up catalog fetch fails.
      queryClient.setQueryData(catalog.queryKey, (templates) =>
        templates?.map((template) => (template.template === saved.template ? saved : template))
      )
      return queryClient.invalidateQueries({ queryKey: [root(organizationId)] })
    },
  })
}
